import { maybe, type Maybe, rd } from '@passionware/monads';
import { ensureError } from '@passionware/platform-js';
import type { Resource, ResourceType } from '__types__';
import type { WorkspaceScoping } from 'v5/api/_common/query/workspace-scoping';
import { contactDetailPathInScope } from 'v5/core/app-scope/contactDetailPathInScope';
import { type AppScope, appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { useWithWorkspaceScoping } from 'v5/core/app-scope/query/useWithWorkspaceScoping';
import { getPrimaryEmail } from 'v5/core/contact-details/primaryEmail';
import { usesSingleNameField } from 'v5/core/entity-schema/resource/fieldSpecs';
import {
  resourceQueryUtils,
  type ResourceSearchQuery
} from 'v5/api/resource/resource.api';
import { scopedResourceQuery } from 'v5/patterns/resource-picker/scopedResourceQuery';
import { resourceDisplayName } from 'v5/patterns/resource/resourceDisplayName';
import { RESOURCE_CORE_FIELD_ICONS } from 'v5/patterns/resource/resourceFieldIcons';
import type {
  EntityCardData,
  EntityCardFact
} from 'v5/patterns/widgets/EntityHoverCard';
import {
  EntityChip,
  EntityOption,
  type EntityOptionData
} from 'v5/patterns/widgets/EntityOption';
import { ensureIdleQuery } from 'v5/platform/ensureIdleQuery';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import { Permission } from 'v5/services/front/PermissionService/PermissionService';
import type { WithToastService } from 'v5/services/front/ToastService/ToastService';
import type { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import type { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import type { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import type { WithResourceMutationService } from 'v5/services/io/ResourceMutationService/ResourceMutationService';
import type { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';

export type ResourceReferenceServices = WithServices<
  [
    WithResourceService,
    /** Opening a referenced resource. */
    WithUserFlowService,
    /** A failed inline create. */
    WithToastService,
    /** Browsing scope for the search, the create and the page link. */
    WithAppScopeService,
    WithAccessService,
    WithResourceTypeService,
    WithResourceMutationService
  ]
>;

/**
 * Whose resources are offered. Absent: the browsing scope (the URL's). An entity's own
 * workspace for a picker inside that entity (a callsheet's); `scoping` absent while it is
 * not known offers nothing, never the organization's.
 */
export type ResourceReferenceScope = {
  type: 'entity';
  scoping: Maybe<WorkspaceScoping>;
};

export type ResourceReferenceFilters = Partial<ResourceSearchQuery['filters']>;

/**
 * How a picker finds, shows, opens and creates a referenced resource in the
 * browsing scope. The schema form's `entity-ref` fields and the v1 pickers
 * share it.
 *
 * @param getAppScope read when the sidebar opens.
 */
export function createResourceReferenceSource(
  services: ResourceReferenceServices['services'],
  getAppScope: () => AppScope
) {
  return {
    noun: 'resource',
    useOption: (id: number | null) =>
      ensureIdleQuery(
        id,
        rd.map(
          services.resourceService.useItem(maybe.getOrUndefined(id)),
          item => <EntityChip entity={resourceAsEntity(item)} />
        )
      ),
    useCard: (id: number | null) =>
      ensureIdleQuery(
        id,
        rd.map(
          services.resourceService.useItem(maybe.getOrUndefined(id)),
          resourceCard
        )
      ),
    useOptions: (
      query: string,
      filters: ResourceReferenceFilters,
      scope?: ResourceReferenceScope
    ) => {
      const searchQuery = resourceQueryUtils
        .transform(resourceQueryUtils.ofDefault())
        .build(b => [b.withSearch(query), b.withMergedFilters(filters)]);
      const browsing = useWithWorkspaceScoping(
        services.appScopeService.useResolvedAppScope(),
        searchQuery
      );
      return rd.map(
        services.resourceService.useList(
          scope?.type === 'entity'
            ? scopedResourceQuery(searchQuery, scope.scoping)
            : browsing
        ),
        list =>
          rowLevelValidation.validDataFromRows(list.items).map(item => ({
            value: item.id,
            label: <EntityOption entity={resourceAsEntity(item)} />
          }))
      );
    },
    // Straight from the typed name, into the first type `isCreatable` accepts,
    // visible where the user browses.
    useCreate: (isCreatable: (type: ResourceType) => boolean) => {
      const scopeRd = services.appScopeService.useResolvedAppScope();
      const identity = rd.tryMap(scopeRd, appScopeUtils.identityFromResolved);
      const canCreate = rd.tryGet(
        services.accessService.useCan(identity)({
          action: Permission.RESOURCE_CREATE
        })
      );
      const type = rd
        .tryGet(services.resourceTypeService.useResourceTypes(identity))
        ?.find(isCreatable);
      const scope = rd.tryGet(scopeRd);
      if (canCreate !== true || !type?.model || !scope) return null;
      const model = type.model;
      return async (name: string) => {
        const [first = '', ...rest] = name.split(/\s+/);
        const changes = usesSingleNameField(model)
          ? [{ column: 'first_name' as const, value: name }]
          : [
              { column: 'first_name' as const, value: first },
              { column: 'last_name' as const, value: rest.join(' ') }
            ];
        try {
          const created = await services.resourceMutationService.create({
            resourceTypeId: type.id,
            profilePicture: null,
            membership:
              scope.level === 'organization'
                ? { type: 'global' }
                : { type: 'workspaces', workspaceIds: [scope.workspaceId] },
            patches: [{ kind: 'core', changes }]
          });
          return created.id;
        } catch (cause) {
          services.toastService.open({
            type: 'error',
            message: ensureError(cause).message
          });
          return null;
        }
      };
    },
    open: (id: number) =>
      services.userFlowService.openResourceSidebar(id, {
        appScope: getAppScope()
      }),
    // As the resource lists: the page in the browsing scope.
    usePageHref: (id: number) => contactDetailPathInScope(getAppScope(), id)
  };
}

/** A referenced resource in a picker: its picture, name, and its type and email under it. */
function resourceAsEntity(
  resource: Pick<
    Resource,
    | 'full_name'
    | 'first_name'
    | 'last_name'
    | 'profile_picture'
    | 'resource_type'
    | 'emails'
  >
): EntityOptionData {
  const detail = [
    resource.resource_type?.name,
    getPrimaryEmail(resource.emails ?? [])
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    name: resourceDisplayName(resource),
    detail: detail === '' ? null : detail,
    pictureUrl: resource.profile_picture ?? null
  };
}

/** A referenced resource on its hover card: what it is, how to reach it, what it is marked with. */
export function resourceCard(
  resource: Pick<
    Resource,
    | 'full_name'
    | 'first_name'
    | 'last_name'
    | 'pronouns'
    | 'profile_picture'
    | 'resource_type'
    | 'organisation'
    | 'emails'
    | 'phone_numbers'
    | 'websites'
    | 'locations'
    | 'groups'
    | 'tags'
  >
): EntityCardData {
  const filled = (rows: Resource['emails'] | undefined) =>
    (rows ?? []).filter(row => (row.value_1 ?? '').trim() !== '');
  const primary = getPrimaryEmail(resource.emails ?? []);
  // The primary email first, the others as saved.
  const emails = [...filled(resource.emails)].sort(
    (a, b) =>
      Number(b.value_1?.trim() === primary) -
      Number(a.value_1?.trim() === primary)
  );
  /** The first of a kind, and how many more there are. */
  const firstOf = (
    rows: ReadonlyArray<Omit<EntityCardFact, 'more'>>
  ): EntityCardFact[] =>
    rows.length === 0 ? [] : [{ ...rows[0], more: rows.length - 1 }];
  const facts: EntityCardFact[] = [
    ...firstOf(
      emails.map(row => ({
        icon: <RESOURCE_CORE_FIELD_ICONS.emails aria-hidden />,
        text: row.value_1?.trim() ?? '',
        href: `mailto:${row.value_1?.trim() ?? ''}`
      }))
    ),
    ...firstOf(
      filled(resource.phone_numbers).map(row => ({
        icon: <RESOURCE_CORE_FIELD_ICONS.phone_numbers aria-hidden />,
        text: row.value_1?.trim() ?? '',
        href: `tel:${(row.value_1 ?? '').replace(/[^\d+]/g, '')}`
      }))
    ),
    ...firstOf(
      filled(resource.websites).map(row => {
        const site = row.value_1?.trim() ?? '';
        return {
          icon: <RESOURCE_CORE_FIELD_ICONS.websites aria-hidden />,
          text: site,
          href: /^https?:\/\//.test(site) ? site : `https://${site}`
        };
      })
    ),
    ...firstOf(
      (resource.locations ?? [])
        .map(location => location.name?.trim() ?? '')
        .filter(name => name !== '')
        .map(name => ({
          icon: <RESOURCE_CORE_FIELD_ICONS.locations aria-hidden />,
          text: name,
          href: null
        }))
    )
  ];
  const chips = [
    ...(resource.groups ?? [])
      .filter(group => (group.name ?? '').trim() !== '')
      .map(group => ({
        label: group.name ?? '',
        colour: group.colour ?? null
      })),
    ...(resource.tags ?? []).map(tag => ({ label: tag.tag, colour: null }))
  ];
  const organisation = resource.organisation;
  return {
    name: resourceDisplayName(resource),
    pictureUrl: resource.profile_picture ?? null,
    kind: resource.resource_type?.name?.trim() || null,
    note: resource.pronouns?.trim() || null,
    partOf: organisation
      ? {
          name: resourceDisplayName(organisation),
          pictureUrl: organisation.profile_picture ?? null
        }
      : null,
    facts,
    chips
  };
}
